<template>
    <card>
        <template #header>
            <custom-card-header
                :href="$route('assets.stakeholders.edit', asset)"
                :label="trans('general.action.edit')"
                :show-action="hasAnyPermission('asset.stakeholder.update')"
                :title="trans('stakeholders.overview.title')"
                action-icon="cil-pencil"
            />
        </template>
        <dl>
            <div class="grid grid-cols-1 gap-1 py-3 md:grid-cols-3">
                <dt class="font-bold">
                    {{ trans('asset.stakeholders.owners.label') }}
                </dt>
                <dd class="md:col-span-2">
                    <template v-if="asset.owners?.length">
                        <stakeholder-base-information
                            v-for="owner in asset.owners"
                            :key="owner.id"
                            :collapsible="true"
                            :contacts="owner.contacts"
                            :contacts-title="trans('owners.overview.contacts.title')"
                            :description="owner.description"
                            :name="owner.name"
                        />
                    </template>
                    <template v-else>{{ trans('general.not_available') }}</template>
                </dd>
            </div>
            <div class="grid grid-cols-1 gap-1 py-3 md:grid-cols-3">
                <dt class="font-bold">
                    {{ trans('asset.stakeholders.operators.label') }}
                </dt>
                <dd class="md:col-span-2">
                    <template v-if="asset.operators?.length">
                        <stakeholder-base-information
                            v-for="operator in asset.operators"
                            :key="operator.id"
                            :collapsible="true"
                            :contacts="operator.contacts"
                            :contacts-title="trans('operators.overview.contacts.title')"
                            :description="operator.description"
                            :name="operator.name"
                        />
                    </template>
                    <template v-else>{{ trans('general.not_available') }}</template>
                </dd>
            </div>
            <div class="grid grid-cols-1 gap-1 py-3 md:grid-cols-3">
                <dt class="font-bold">
                    {{ trans('asset.stakeholders.maintainers.label') }}
                </dt>
                <dd class="md:col-span-2">
                    <template v-if="asset.maintainers?.length">
                        <stakeholder-base-information
                            v-for="maintainer in asset.maintainers"
                            :key="maintainer.id"
                            :collapsible="true"
                            :contacts="maintainer.businessContacts"
                            :contacts-title="trans('maintainers.overview.contacts.title')"
                            :description="maintainer.description"
                            :name="maintainer.name"
                            :show-expiration-warning="showExpirationWarning(maintainer)"
                        >
                            <service-level-agreement-info
                                v-for="(sla, index) in maintainer.serviceLevelAgreements"
                                :key="sla.id"
                                :class="{
                                    'border-b pb-2':
                                        maintainer.serviceLevelAgreements.length > 1 && index < maintainer.serviceLevelAgreements.length - 1,
                                }"
                                :sla="sla"
                                :support-contacts="maintainer.supportContacts"
                                class="py-2"
                            />
                        </stakeholder-base-information>
                    </template>
                    <template v-else>{{ trans('general.not_available') }}</template>
                </dd>
            </div>
        </dl>
    </card>
</template>
<script setup>
import CustomCardHeader from '@/Components/CustomCardHeader.vue';
import StakeholderBaseInformation from '@/Pages/Assets/Details/StakeholderBaseInformation.vue';
import ServiceLevelAgreementInfo from '@/Pages/Stakeholders/Maintainers/ServiceLevelAgreementInfo.vue';
import Card from '@/Components/Card.vue';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import { trans } from '@/mixins/i18n.js';
import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';

const page = usePage();
const asset = computed(() => page.props.asset);

const showExpirationWarning = (maintainer) => {
    return maintainer.serviceLevelAgreements?.some((sla) => sla.expired || sla.expiresSoon || sla.missing);
};
</script>
